import { labelSymbolSvg } from '@/lib/labelSymbol'
import { escapeHtml } from '@/lib/printLabel'

/**
 * The shared visual system for every printable label.
 *
 * One module so an inventory lot, a shelf and a machine read the same way round:
 * role tag → symbol → identifier → readable code. A technician who has learnt
 * one label has learnt all of them, and can tell them apart across a room by the
 * inverted role tag alone.
 *
 * Kept apart from the per-label content modules deliberately. Those decide WHAT
 * a label says, which differs per subject; this decides HOW it says it, which
 * must not.
 *
 * A pure module returning HTML strings, for the same reason `pickSheetHtml.ts`
 * is one: the print path writes markup into a hidden iframe where a React tree
 * cannot render, and this repo's vitest config only collects `src/**\/*.test.ts`
 * in a node environment, so logic left in a `.tsx` would never be tested at all.
 */

export const LABEL_STYLE =
  '@page{margin:10mm}' +
  'body{font-family:ui-sans-serif,system-ui,-apple-system,sans-serif;margin:0;padding:16px;' +
  '-webkit-print-color-adjust:exact;print-color-adjust:exact}' +
  // ~100mm wide. Sized for reading at arm's length off a shelf, through a
  // freezer door, or on the side of a machine — not for fitting the most labels
  // per sheet.
  '.lbl{width:380px;padding:16px 18px 18px;border:1.5px solid #111;' +
  'page-break-inside:avoid;break-inside:avoid;margin:0 0 14px;background:#fff}' +
  // Role on the left as an inverted block, qualifiers on the right. The
  // inversion is the one bold move on the label, and it earns its place: it is
  // what distinguishes a lot label from a shelf label from an asset tag at a
  // glance, before any text is read.
  '.hd{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:14px}' +
  '.tag{background:#111;color:#fff;font-size:11px;font-weight:700;letter-spacing:0.16em;' +
  'padding:4px 9px;text-transform:uppercase}' +
  '.hd-meta{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11px;' +
  'font-weight:600;letter-spacing:0.04em;color:#333;text-transform:uppercase;text-align:right}' +
  // Symbol first in the reading order, because scanning is what the label is
  // for; the identity sits beside it for the human half.
  '.body{display:flex;align-items:flex-start;gap:16px}' +
  '.sym{flex:0 0 auto;line-height:0}' +
  '.idw{min-width:0;flex:1 1 auto}' +
  '.id{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:30px;font-weight:700;' +
  'line-height:1.05;letter-spacing:-0.02em;color:#111;word-break:break-all}' +
  '.sub{font-size:13px;line-height:1.35;color:#333;margin-top:6px}' +
  // The human-readable interpretation of the symbol. Required by GS1 and ISBT
  // 128 alike, and the only thing that keeps a damaged label usable.
  '.hri{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11px;font-weight:600;' +
  'letter-spacing:0.02em;color:#555;margin-top:8px;word-break:break-all}' +
  // One hairline, dividing the thing from where it lives.
  '.place{margin-top:14px;padding-top:12px;border-top:1.5px solid #cfcfcf;' +
  'display:flex;align-items:baseline;justify-content:space-between;gap:10px}' +
  '.k{font-size:11px;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;color:#555}' +
  '.v{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:18px;font-weight:700;' +
  'color:#111;text-align:right;word-break:break-all}' +
  // A place path ("MAIN › Building A › Floor 1 › ISO 7 Cleanroom") is read, not
  // matched character by character like a slot code: plainer and smaller, and
  // it wraps between words rather than through them.
  '.v.path{font-family:inherit;font-size:13px;font-weight:600;line-height:1.35;' +
  'word-break:normal;overflow-wrap:anywhere}'

/** Role tag on the left, qualifiers on the right. */
export function labelHeader(role: string, meta: string): string {
  return (
    `<div class="hd"><span class="tag">${escapeHtml(role)}</span>` +
    (meta ? `<span class="hd-meta">${escapeHtml(meta)}</span>` : '') +
    `</div>`
  )
}

/**
 * Symbol beside the identity, with the encoded value underneath.
 *
 * `id` is what a PERSON reads and `code` is what the SCANNER reads, and they are
 * deliberately allowed to differ. An equipment tag shows `APH-A4` in the hero
 * while encoding an opaque immutable id — the operator gets the name they use,
 * the machine gets something a rename cannot invalidate.
 */
export function labelBody(code: string | null, id: string, sub: string): string {
  const symbol = code ? labelSymbolSvg(code, { moduleSize: 4 }) : ''
  return (
    `<div class="body">` +
    (symbol ? `<div class="sym">${symbol}</div>` : '') +
    `<div class="idw"><div class="id">${escapeHtml(id)}</div>` +
    (sub ? `<div class="sub">${escapeHtml(sub)}</div>` : '') +
    `</div></div>` +
    (code ? `<div class="hri">${escapeHtml(code)}</div>` : '')
  )
}

/**
 * A captioned value below the hairline — where the thing lives. `code` (the
 * default) is a short slot code set large; `path` is a place path, set to read.
 */
export function labelFooterRow(caption: string, value: string, kind: 'code' | 'path' = 'code'): string {
  return (
    `<div class="place"><span class="k">${escapeHtml(caption)}</span>` +
    `<span class="${kind === 'path' ? 'v path' : 'v'}">${escapeHtml(value)}</span></div>`
  )
}

/** Several labels on one sheet, for the batch print actions. */
export function labelSheetHtml(labels: readonly string[]): string {
  return labels.join('')
}
